import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
    Gift, Sparkles, TrendingUp, DollarSign, Lock, Trophy, AlertCircle,
    History, X, Crown, Award, Clock, ArrowLeft, Users, ArrowUpRight, Info, CheckCircle2
} from 'lucide-react';
import axios from 'axios';
import Nt from './Nt';

const API_BASE = 'https://api.light-platform.com/api';

/**
 * Лотереи повышенного номинала (2$ и 4$).
 *
 * Отдельный компонент от LightLotoCluster (лотерея 1$) — та не затрагивается.
 * Таблица призов не захардкожена: подтягивается с /lottery/{tier}/prizes,
 * поэтому при изменении номиналов на бэке фронт править не нужно.
 *
 * Использование:
 *   <LightLotoProCluster tier={2} onBack={...} />
 *   <LightLotoProCluster tier={4} onBack={...} />
 */

const TIER_THEME = {
    2: {
        name: 'Light Loto 2',
        accent: 'text-violet-600',
        headerFrom: 'from-violet-500',
        headerTo: 'to-indigo-600',
        pageBg: 'from-[#ede9fe] via-[#c4b5fd] to-[#4c1d95]',
        buttonFrom: 'from-violet-600',
        buttonTo: 'to-indigo-600',
        ring: 'focus-visible:ring-violet-400',
        chip: 'bg-violet-100 text-violet-700',
    },
    4: {
        name: 'Light Loto 4',
        accent: 'text-rose-600',
        headerFrom: 'from-rose-500',
        headerTo: 'to-orange-600',
        pageBg: 'from-[#ffe4e6] via-[#fda4af] to-[#881337]',
        buttonFrom: 'from-rose-600',
        buttonTo: 'to-orange-600',
        ring: 'focus-visible:ring-rose-400',
        chip: 'bg-rose-100 text-rose-700',
    },
};

const CARDS_COUNT = 12;

const LightLotoProCluster = ({ tier = 2, onBack }) => {
    const theme = TIER_THEME[tier] || TIER_THEME[2];

    const [gameState, setGameState] = useState('idle');
    const [cards, setCards] = useState(Array(CARDS_COUNT).fill(null));
    const [revealedCards, setRevealedCards] = useState([]);
    const [matchedSymbol, setMatchedSymbol] = useState(null);
    const [prize, setPrize] = useState(null);
    const [referralBreakdown, setReferralBreakdown] = useState([]);
    const [ticketUuid, setTicketUuid] = useState(null);

    const [isProcessing, setIsProcessing] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const [notice, setNotice] = useState(null);

    const [info, setInfo] = useState(null);
    const [prizeList, setPrizeList] = useState([]);
    const [stats, setStats] = useState(null);
    const [recentWinners, setRecentWinners] = useState([]);

    const [showHistory, setShowHistory] = useState(false);
    const [historyTab, setHistoryTab] = useState('tickets');
    const [ticketHistory, setTicketHistory] = useState([]);
    const [referralHistory, setReferralHistory] = useState([]);

    const authHeaders = () => ({
        headers: { Authorization: `Bearer ${localStorage.getItem('auth_token')}` },
    });

    const url = useCallback((path) => `${API_BASE}/lottery/${tier}${path}`, [tier]);

    const showNotice = (text, type = 'error') => {
        setNotice({ text, type });
        setTimeout(() => setNotice(null), 6000);
    };

    // ------------------------------------------------------------------
    //  Загрузка данных
    // ------------------------------------------------------------------

    const loadInfo = useCallback(async () => {
        try {
            const { data } = await axios.get(url('/info'), authHeaders());
            if (data.success) setInfo(data.data);
        } catch (e) {
            console.error('lottery info', e);
        }
    }, [url]);

    const loadPrizes = useCallback(async () => {
        try {
            const { data } = await axios.get(url('/prizes'), authHeaders());
            if (data.success) setPrizeList(data.data.prizes || []);
        } catch (e) {
            console.error('lottery prizes', e);
        }
    }, [url]);

    const loadStats = useCallback(async () => {
        try {
            const { data } = await axios.get(url('/stats'), authHeaders());
            if (data.success) {
                setStats(data.data);
                setRecentWinners(data.data.recent_winners || []);
            }
        } catch (e) {
            console.error('lottery stats', e);
        }
    }, [url]);

    useEffect(() => {
        let active = true;
        setIsLoading(true);
        // при смене номинала полностью сбрасываем игру
        setGameState('idle');
        setCards(Array(CARDS_COUNT).fill(null));
        setRevealedCards([]);
        setMatchedSymbol(null);
        setPrize(null);
        setReferralBreakdown([]);
        setTicketUuid(null);

        Promise.all([loadInfo(), loadPrizes(), loadStats()]).finally(() => {
            if (active) setIsLoading(false);
        });

        return () => { active = false; };
    }, [tier, loadInfo, loadPrizes, loadStats]);

    // ------------------------------------------------------------------
    //  Карта символов строится из ответа API, а не хардкодом
    // ------------------------------------------------------------------

    const symbolMap = useMemo(() => {
        const price = info?.ticket_price || tier;
        const map = {};

        prizeList.forEach((p) => {
            if (p.type === 'jackpot') {
                map.jackpot = {
                    icon: '💎',
                    label: 'ДЖЕКПОТ',
                    color: 'from-yellow-400 to-orange-500',
                };
                return;
            }

            const amount = p.card_amount;
            let icon = '💵';
            let color = 'from-green-400 to-emerald-500';

            if (amount > price * 5) {
                icon = '🤑';
                color = 'from-red-400 to-rose-500';
            } else if (amount > price) {
                icon = '💰';
                color = 'from-purple-400 to-pink-500';
            } else if (amount > price * 0.4) {
                icon = '💸';
                color = 'from-blue-400 to-cyan-500';
            }

            map[p.symbol] = {
                icon,
                color,
                label: `${Number(amount).toFixed(2)} USDT`,
                cardAmount: amount,
                grossAmount: p.gross_amount,
                referralAmount: p.referral_amount,
                probability: p.probability,
            };
        });

        return map;
    }, [prizeList, info, tier]);

    /**
     * Призы сгруппированы по номиналу, как в лотерее 1$: четыре диапазона
     * вместо два десятка отдельных строк.
     *
     * Границы заданы кратно цене билета, поэтому автоматически совпадают с
     * группами A/B/C/D из config/lottery.php на бэке при любом множителе.
     * Если будете перетряхивать таблицу призов — правится здесь, в GROUP_DEFS.
     *
     * Проценты нормируются по денежным призам, чтобы группы давали в сумме 100%.
     */
    const prizeGroups = useMemo(() => {
        const money = prizeList.filter((p) => p.type === 'money');
        if (!money.length) return [];

        const total = money.reduce((s, p) => s + (p.probability || 0), 0) || 1;
        const base = info?.ticket_price || tier;

        const GROUP_DEFS = [
            { key: 'a', icon: '\uD83D\uDCB5', upTo: base * 2, color: 'text-green-600' },
            { key: 'b', icon: '\uD83D\uDCB0', upTo: base * 8, color: 'text-purple-600' },
            { key: 'c', icon: '\uD83E\uDD11', upTo: Infinity, color: 'text-red-600' },
        ];

        const groups = [];
        let lower = -Infinity;

        GROUP_DEFS.forEach((def) => {
            const items = money.filter(
                (p) => p.gross_amount > lower && p.gross_amount <= def.upTo
            );
            lower = def.upTo;

            if (!items.length) return;

            const amounts = items.map((i) => i.card_amount);

            groups.push({
                key: def.key,
                icon: def.icon,
                color: def.color,
                from: Math.min(...amounts),
                to: Math.max(...amounts),
                chance: (items.reduce((sum, i) => sum + (i.probability || 0), 0) / total) * 100,
            });
        });

        return groups;
    }, [prizeList, info, tier]);

    // ------------------------------------------------------------------
    //  Игровые действия
    // ------------------------------------------------------------------

    const price = info?.ticket_price ?? tier;
    const balance = info?.user_balance ?? 0;
    const canBuy = balance >= price;

    const purchaseTicket = async () => {
        if (isProcessing || gameState !== 'idle') return;

        if (!canBuy) {
            showNotice(`Не хватает ${(price - balance).toFixed(2)} USDT. Пополните баланс.`);
            return;
        }

        setIsProcessing(true);
        try {
            const { data } = await axios.post(url('/purchase-ticket'), {}, authHeaders());

            if (data.success) {
                setTicketUuid(data.data.ticket_uuid);
                setInfo((prev) => (prev ? {
                    ...prev,
                    user_balance: data.data.new_balance,
                    jackpot_amount: data.data.jackpot_amount,
                } : prev));

                setGameState('playing');
                setRevealedCards([]);
                setCards(Array(CARDS_COUNT).fill(null));
                setMatchedSymbol(null);
                setPrize(null);
                setReferralBreakdown([]);
            } else {
                showNotice(data.message || 'Не удалось купить билет');
            }
        } catch (e) {
            showNotice(e.response?.data?.message || 'Не удалось купить билет. Попробуйте позже.');
        } finally {
            setIsProcessing(false);
        }
    };

    const revealCard = async (index) => {
        if (isProcessing || gameState !== 'playing' || revealedCards.includes(index)) return;

        setIsProcessing(true);
        try {
            const { data } = await axios.post(
                url('/reveal-card'),
                { card_index: index, ticket_uuid: ticketUuid },
                authHeaders()
            );

            if (!data.success) {
                showNotice(data.message || 'Не удалось открыть карточку');
                return;
            }

            const d = data.data;

            setCards((prev) => {
                const next = [...prev];
                next[index] = d.symbol;
                return next;
            });
            setRevealedCards((prev) => [...prev, index]);

            if (d.is_winning) {
                setMatchedSymbol(d.winning_symbol);
                setPrize(d.prize);
                setReferralBreakdown(d.referral_breakdown || []);

                if (typeof d.new_balance === 'number') {
                    setInfo((prev) => (prev ? { ...prev, user_balance: d.new_balance } : prev));
                }

                setTimeout(() => {
                    setGameState('revealing');
                    setCards(d.all_cards);
                    setRevealedCards(Array.from({ length: CARDS_COUNT }, (_, i) => i));

                    setTimeout(() => {
                        setGameState('finished');
                        loadInfo();
                        loadStats();
                    }, 1500);
                }, 500);
            }
        } catch (e) {
            showNotice(e.response?.data?.message || 'Не удалось открыть карточку. Попробуйте позже.');
        } finally {
            setIsProcessing(false);
        }
    };

    const playAgain = () => {
        setGameState('idle');
        setCards(Array(CARDS_COUNT).fill(null));
        setRevealedCards([]);
        setMatchedSymbol(null);
        setPrize(null);
        setReferralBreakdown([]);
        setTicketUuid(null);
    };

    const openHistory = async (tab = 'tickets') => {
        setHistoryTab(tab);
        setShowHistory(true);

        try {
            if (tab === 'tickets') {
                const { data } = await axios.get(url('/user-tickets'), authHeaders());
                if (data.success) setTicketHistory(data.data.tickets || []);
            } else {
                const { data } = await axios.get(url('/referral-payouts'), authHeaders());
                if (data.success) setReferralHistory(data.data.payouts || []);
            }
        } catch (e) {
            showNotice('Не удалось загрузить историю');
        }
    };

    const restoreTicket = async (ticket) => {
        if (ticket.status !== 'active') {
            showNotice('Этот билет уже завершён');
            return;
        }

        setIsProcessing(true);
        try {
            const { data } = await axios.get(url(`/restore-ticket/${ticket.ticket_uuid}`), authHeaders());

            if (data.success) {
                setTicketUuid(data.data.ticket_uuid);
                setCards(data.data.cards || Array(CARDS_COUNT).fill(null));
                setRevealedCards(data.data.revealed_cards || []);
                setGameState('playing');
                setShowHistory(false);
                showNotice('Билет открыт — продолжайте', 'success');
            }
        } catch (e) {
            showNotice(e.response?.data?.message || 'Не удалось вернуться к билету');
        } finally {
            setIsProcessing(false);
        }
    };

    // ------------------------------------------------------------------
    //  Утилиты отображения
    // ------------------------------------------------------------------

    const formatTimeAgo = (dateString) => {
        if (!dateString) return 'Недавно';
        const date = new Date(dateString);
        const diffMs = new Date() - date;
        const mins = Math.floor(diffMs / 60000);
        const hours = Math.floor(diffMs / 3600000);
        const days = Math.floor(diffMs / 86400000);

        if (mins < 1) return 'Только что';
        if (mins < 60) return `${mins} мин. назад`;
        if (hours < 24) return `${hours} ч. назад`;
        if (days < 7) return `${days} дн. назад`;
        return date.toLocaleDateString('ru-RU');
    };

    const refPercent = info?.referral?.total_percent
        ? Math.round(info.referral.total_percent * 100)
        : 15;
    const levelPercent = info?.referral?.levels
        ? Math.round((Object.values(info.referral.levels)[0] || 0.05) * 100)
        : 5;

    // ------------------------------------------------------------------
    //  Рендер
    // ------------------------------------------------------------------

    if (isLoading) {
        return (
            <div className={`min-h-screen bg-gradient-to-br ${theme.pageBg} p-4 md:p-8 flex items-center justify-center`}>
                <div className="bg-white rounded-2xl shadow-2xl px-8 py-6 text-center">
                    <Sparkles className={`w-10 h-10 mx-auto mb-3 animate-spin ${theme.accent}`} />
                    <p className="text-gray-600">Загружаем лотерею…</p>
                </div>
            </div>
        );
    }

    return (
        <div className={`min-h-screen bg-gradient-to-br ${theme.pageBg} p-4 md:p-8`}>
            <div className="max-w-6xl mx-auto">

                {/* ---------- Шапка ---------- */}
                <div className="bg-white rounded-2xl shadow-2xl p-6 mb-6">
                    {onBack && (
                        <button
                            onClick={onBack}
                            className="flex items-center gap-2 text-gray-500 hover:text-gray-800 transition-colors mb-4"
                        >
                            <ArrowLeft className="w-4 h-4" />
                            <span className="text-sm font-medium">К кластерам</span>
                        </button>
                    )}

                    <div className="flex flex-col md:flex-row justify-between items-center gap-4">
                        <div>
                            <div className="flex items-center gap-3">
                                <h1 className={`text-3xl md:text-4xl font-bold ${theme.accent} flex items-center gap-2`}>
                                    <Sparkles className="w-8 h-8" />
                                    {theme.name}
                                </h1>
                                <span className={`text-xs font-bold px-2 py-1 rounded-full ${theme.chip}`}>
                                    {Number(price).toFixed(0)}$
                                </span>
                            </div>
                            <p className="text-gray-600 mt-2">
                                Откройте 3 одинаковых символа и заберите приз
                            </p>
                            <p className="text-sm text-gray-500 mt-1">
                                Призы в {info?.multiplier || tier} раза крупнее, чем в лотерее за 1$
                            </p>
                        </div>

                        <div className="flex flex-col gap-3 w-full md:w-auto">
                            <div className="bg-gradient-to-r from-yellow-400 to-orange-500 rounded-xl p-4 text-white text-center">
                                <div className="flex items-center justify-center gap-2 mb-1">
                                    <Gift className="w-5 h-5" />
                                    <span className="text-sm font-semibold">ДЖЕКПОТ</span>
                                </div>
                                <div className="text-2xl font-bold">
                                    {Number(info?.jackpot_amount || 0).toFixed(2)} USDT
                                </div>
                            </div>

                            <div className={`bg-gradient-to-r ${theme.headerFrom} ${theme.headerTo} rounded-xl p-4 text-white text-center`}>
                                <div className="flex items-center justify-center gap-2 mb-1">
                                    <DollarSign className="w-5 h-5" />
                                    <span className="text-sm font-semibold">ВАШ БАЛАНС</span>
                                </div>
                                <div className="text-2xl font-bold">{Number(balance).toFixed(2)} USDT</div>
                            </div>
                        </div>
                    </div>

                    <div className="mt-4 flex flex-wrap justify-end gap-2">
                        <button
                            onClick={() => openHistory('referrals')}
                            className="flex items-center gap-2 px-4 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
                        >
                            <Users className="w-4 h-4" />
                            <span className="text-sm font-medium">Реферальные начисления</span>
                        </button>
                        <button
                            onClick={() => openHistory('tickets')}
                            className="flex items-center gap-2 px-4 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
                        >
                            <History className="w-4 h-4" />
                            <span className="text-sm font-medium">История билетов</span>
                        </button>
                    </div>
                </div>

                {/* ---------- Уведомление ---------- */}
                {notice && (
                    <div
                        role="status"
                        className={`rounded-xl p-4 mb-6 flex items-start gap-3 shadow-lg ${
                            notice.type === 'success'
                                ? 'bg-emerald-50 border border-emerald-200 text-emerald-800'
                                : 'bg-red-50 border border-red-200 text-red-800'
                        }`}
                    >
                        <AlertCircle className="w-5 h-5 flex-shrink-0 mt-0.5" />
                        <p className="text-sm font-medium">{notice.text}</p>
                    </div>
                )}

                {/* ---------- Как делится выигрыш (фирменный блок) ---------- */}
                {info?.referral?.enabled && (
                    <div className="bg-white rounded-2xl shadow-2xl p-6 mb-6">
                        <div className="flex items-center gap-2 mb-4">
                            <ArrowUpRight className={`w-6 h-6 ${theme.accent}`} />
                            <h3 className={`text-xl font-bold ${theme.accent}`}>Как делится выигрыш</h3>
                        </div>

                        <p className="text-sm text-gray-600 mb-4">
                            На карточке написано ровно то, что придёт вам на баланс. Сверх этого
                            лотерея начисляет по {levelPercent}% трём ближайшим наставникам над вами,
                            набравшим оборот по билетам — и вы получаете столько же с выигрышей
                            своей структуры. Если наставник оборот не набрал, его доля переходит
                            следующему активному выше по ветке.
                        </p>

                        <div className="grid grid-cols-1 sm:grid-cols-4 gap-3">
                            <div className={`rounded-xl p-4 text-white bg-gradient-to-br ${theme.headerFrom} ${theme.headerTo}`}>
                                <p className="text-xs opacity-90">Вам на баланс</p>
                                <p className="text-2xl font-bold">{100 - refPercent}%</p>
                                <p className="text-xs opacity-90 mt-1">
                                    например, {Number(price / 2).toFixed(2)} → {(price / 2 * (1 - refPercent / 100)).toFixed(2)}
                                </p>
                            </div>
                            {[1, 2, 3].map((lvl) => (
                                <div key={lvl} className="rounded-xl p-4 bg-gray-50 border border-gray-200">
                                    <p className="text-xs text-gray-500">{lvl}-й уровень вверх</p>
                                    <p className="text-2xl font-bold text-gray-800">{levelPercent}%</p>
                                    <p className="text-xs text-gray-500 mt-1">
                                        {lvl === 1 ? 'ближайший активный' : `${lvl}-й активный вверх`}
                                    </p>
                                </div>
                            ))}
                        </div>

                        {/* Статус квалификации: набран ли оборот по билетам за окно */}
                        {info?.qualification?.enabled && info.qualification.in_force && (
                            info.qualification.qualified ? (
                                <div className="mt-4 flex items-start gap-2 text-sm text-emerald-700 bg-emerald-50 rounded-lg p-3">
                                    <CheckCircle2 className="w-4 h-4 flex-shrink-0 mt-0.5" />
                                    <span>
                                        Реферальные вам начисляются.
                                        {info.qualification.expires_at && (
                                            <> Куплено билетов на{' '}
                                            <span className="font-semibold">
                                                {Number(info.qualification.volume).toFixed(2)} USDT
                                            </span>{' '}
                                            за {info.qualification.window_days} дней — этого хватает до{' '}
                                            <span className="font-semibold">
                                                {new Date(info.qualification.expires_at).toLocaleDateString('ru-RU', {
                                                    day: 'numeric', month: 'long',
                                                })}
                                            </span>.</>
                                        )}
                                    </span>
                                </div>
                            ) : (
                                <div className="mt-4 flex items-start gap-2 text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded-lg p-3">
                                    <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                                    <span>
                                        Сейчас реферальные вам <span className="font-semibold">не начисляются</span>.
                                        Чтобы их получать, нужно купить лотерейных билетов на{' '}
                                        <span className="font-semibold">
                                            {Number(info.qualification.required).toFixed(2)} USDT
                                        </span>{' '}
                                        за {info.qualification.window_days} дней — в любой лотерее.
                                        Куплено {Number(info.qualification.volume).toFixed(2)}, не хватает{' '}
                                        <span className="font-semibold">
                                            {Number(info.qualification.missing).toFixed(2)} USDT
                                        </span>.
                                        Как только доберёте — начисления возобновятся сразу.
                                    </span>
                                </div>
                            )
                        )}

                        {info?.user_referral_earned > 0 && (
                            <div className="mt-4 flex items-center gap-2 text-sm text-emerald-700 bg-emerald-50 rounded-lg p-3">
                                <Award className="w-4 h-4" />
                                Вы уже заработали{' '}
                                <span className="font-bold">
                                    {Number(info.user_referral_earned).toFixed(2)} USDT
                                </span>{' '}
                                с выигрышей своей структуры
                            </div>
                        )}
                    </div>
                )}

                {/* ---------- Последний победитель джекпота ---------- */}
                {info?.last_jackpot_winner && (
                    <div className="bg-gradient-to-r from-yellow-400 via-orange-400 to-red-400 rounded-2xl shadow-2xl p-6 mb-6 text-white">
                        <div className="flex items-center gap-3 mb-3">
                            <Crown className="w-8 h-8" />
                            <h3 className="text-2xl font-bold">Последний победитель джекпота</h3>
                        </div>
                        <div className="bg-white/20 backdrop-blur-sm rounded-xl p-4">
                            <div className="flex justify-between items-center">
                                <div>
                                    <p className="text-sm opacity-90">Игрок</p>
                                    <p className="text-xl font-bold"><Nt>{info.last_jackpot_winner.login}</Nt></p>
                                </div>
                                <div className="text-right">
                                    <p className="text-sm opacity-90">Получил</p>
                                    <p className="text-2xl font-bold">
                                        {Number(info.last_jackpot_winner.amount || 0).toFixed(2)} USDT
                                    </p>
                                </div>
                            </div>
                            {info.last_jackpot_winner.date && (
                                <p className="text-sm opacity-75 mt-2 flex items-center gap-1">
                                    <Clock className="w-4 h-4" />
                                    {formatTimeAgo(info.last_jackpot_winner.date)}
                                </p>
                            )}
                        </div>
                    </div>
                )}

                {/* ---------- Экран покупки ---------- */}
                {gameState === 'idle' && (
                    <div className="bg-white rounded-2xl shadow-2xl p-8 text-center">
                        <div className="max-w-md mx-auto">
                            <Gift className={`w-24 h-24 mx-auto mb-6 ${theme.accent}`} />
                            <h2 className="text-2xl font-bold text-gray-800 mb-4">Готовы испытать удачу?</h2>
                            <p className="text-gray-600 mb-6">
                                Стоимость билета:{' '}
                                <span className={`font-bold ${theme.accent}`}>
                                    {Number(price).toFixed(2)} USDT
                                </span>
                            </p>

                            <div className="bg-gray-50 rounded-xl p-6 mb-8 text-left">
                                <p className="text-sm text-gray-700 space-y-2">
                                    <span className="block">🎯 Каждый билет выигрышный</span>
                                    <span className="block">🎁 Откройте 3 одинаковых символа</span>
                                    <span className="block">💵 Сумма на карточке — это сумма к зачислению</span>
                                    <span className="block">👥 Ещё {refPercent}% сверху уходит трём наставникам</span>
                                </p>
                            </div>

                            <button
                                onClick={purchaseTicket}
                                disabled={isProcessing || !canBuy}
                                className={`w-full bg-gradient-to-r ${theme.buttonFrom} ${theme.buttonTo} text-white font-bold py-4 px-8 rounded-xl hover:shadow-lg transform hover:scale-105 transition-all disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none flex items-center justify-center gap-2`}
                            >
                                <TrendingUp className="w-5 h-5" />
                                {isProcessing
                                    ? 'Покупаем…'
                                    : `Купить билет за ${Number(price).toFixed(0)} USDT`}
                            </button>

                            {!canBuy && (
                                <p className="text-sm text-red-600 mt-3">
                                    Не хватает {(price - balance).toFixed(2)} USDT — пополните баланс
                                </p>
                            )}
                        </div>
                    </div>
                )}

                {/* ---------- Игровое поле ---------- */}
                {(gameState === 'playing' || gameState === 'revealing' || gameState === 'finished') && (
                    <div className="bg-white rounded-2xl shadow-2xl p-6">
                        <div className="grid grid-cols-3 md:grid-cols-4 gap-4 md:gap-6 mb-6">
                            {cards.map((symbol, index) => {
                                const isOpen = revealedCards.includes(index);
                                const meta = symbolMap[symbol];

                                return (
                                    <button
                                        key={index}
                                        onClick={() => revealCard(index)}
                                        disabled={isProcessing || isOpen || gameState !== 'playing'}
                                        aria-label={isOpen ? `Открыто: ${meta?.label || symbol}` : `Открыть карточку ${index + 1}`}
                                        className={`aspect-square rounded-2xl transition-all duration-300 transform focus:outline-none focus-visible:ring-4 ${theme.ring} ${
                                            isOpen
                                                ? `bg-gradient-to-br ${meta?.color || 'from-gray-400 to-gray-500'} scale-105 shadow-2xl border-4 border-white`
                                                : `bg-gradient-to-br ${theme.headerFrom} ${theme.headerTo} hover:scale-105 hover:shadow-2xl cursor-pointer shadow-lg`
                                        } ${
                                            matchedSymbol === symbol && isOpen
                                                ? 'ring-4 ring-yellow-400'
                                                : ''
                                        } disabled:cursor-not-allowed flex items-center justify-center relative overflow-hidden`}
                                    >
                                        {isOpen ? (
                                            <div className="text-center p-2 relative z-10">
                                                <div className="text-4xl md:text-5xl mb-2 drop-shadow-lg">
                                                    {meta?.icon || '❓'}
                                                </div>
                                                <div className="text-xs md:text-sm text-white font-bold leading-tight drop-shadow-md px-1">
                                                    {meta?.label || symbol}
                                                </div>
                                            </div>
                                        ) : (
                                            <div className="relative w-full h-full flex items-center justify-center">
                                                <div className="absolute inset-0 bg-gradient-to-br from-white/10 to-transparent rounded-2xl" />
                                                <div className="relative z-10 flex flex-col items-center">
                                                    <Lock className="w-10 h-10 md:w-12 md:h-12 text-white drop-shadow-lg" />
                                                    <div className="mt-2 text-xs text-white/80 font-semibold">?</div>
                                                </div>
                                                <div className="absolute inset-0 border-2 border-white/30 rounded-2xl pointer-events-none" />
                                            </div>
                                        )}
                                    </button>
                                );
                            })}
                        </div>

                        {/* ---------- Результат ---------- */}
                        {gameState === 'finished' && prize && (
                            <div className="bg-gradient-to-r from-yellow-400 via-orange-400 to-red-400 rounded-xl p-6 text-white text-center mb-6">
                                <Sparkles className="w-16 h-16 mx-auto mb-4" />
                                <h2 className="text-3xl md:text-4xl font-bold mb-2">🎉 Поздравляем!</h2>

                                <div className="bg-white/20 backdrop-blur-sm rounded-lg p-4 mb-4">
                                    <p className="text-sm opacity-90 mb-1">Зачислено на баланс</p>
                                    <p className="text-4xl md:text-5xl font-bold">
                                        {Number(prize.card_amount || 0).toFixed(2)} USDT
                                    </p>
                                </div>

                                {/* Раскладка: откуда взялось это число */}
                                {prize.referral_total > 0 && (
                                    <div className="bg-white/15 backdrop-blur-sm rounded-lg p-4 mb-4 text-left">
                                        <p className="text-sm font-semibold mb-3 flex items-center gap-2">
                                            <Info className="w-4 h-4" />
                                            Номинал приза {Number(prize.gross_amount).toFixed(2)} USDT разделён так:
                                        </p>
                                        <div className="space-y-2 text-sm">
                                            <div className="flex justify-between items-center bg-white/20 rounded px-3 py-2">
                                                <span className="font-semibold">Вам</span>
                                                <span className="font-bold">
                                                    {Number(prize.card_amount).toFixed(2)} USDT
                                                </span>
                                            </div>
                                            {referralBreakdown.map((r) => (
                                                <div
                                                    key={r.level}
                                                    className={`flex justify-between items-center rounded px-3 py-2 ${
                                                        r.status === 'paid' ? 'bg-white/10' : 'bg-white/5 opacity-70'
                                                    }`}
                                                >
                                                    <span>
                                                        {r.level}-й уровень
                                                        {r.status === 'paid' && r.login && (
                                                            <> · <Nt>{r.login}</Nt></>
                                                        )}
                                                        {r.status !== 'paid' && ' · нет наставника'}
                                                    </span>
                                                    <span className={r.status === 'paid' ? 'font-semibold' : 'line-through'}>
                                                        {Number(r.amount).toFixed(2)} USDT
                                                    </span>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}

                                <button
                                    onClick={playAgain}
                                    className="mt-2 bg-white text-gray-800 font-bold py-3 px-8 rounded-xl hover:bg-gray-100 transition-all transform hover:scale-105"
                                >
                                    Играть снова
                                </button>
                            </div>
                        )}

                        {gameState === 'playing' && (
                            <div className="text-center">
                                <div className={`inline-block bg-gradient-to-r ${theme.headerFrom} ${theme.headerTo} text-white px-6 py-3 rounded-full mb-2`}>
                                    <p className="font-semibold">
                                        Открыто карточек: {revealedCards.length} / {CARDS_COUNT}
                                    </p>
                                </div>
                                <p className="text-sm text-gray-600 mt-2">
                                    Найдите 3 одинаковых символа
                                </p>
                            </div>
                        )}
                    </div>
                )}

                {/* ---------- Таблица призов ---------- */}
                {prizeGroups.length > 0 && (
                    <div className="bg-white rounded-2xl shadow-2xl p-6 mt-6">
                        <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
                            <h3 className={`text-xl font-bold ${theme.accent} flex items-center gap-2`}>
                                <Trophy className="w-6 h-6" />
                                Таблица призов и вероятностей
                            </h3>
                            <p className="text-xs text-gray-500">
                                Суммы указаны так, как они придут на баланс
                            </p>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                            <div className="space-y-2">
                                <div className="flex justify-between items-center p-3 bg-gradient-to-r from-yellow-50 to-orange-50 rounded-lg border-l-4 border-yellow-500">
                                    <span className="flex items-center gap-2">
                                        <span className="text-2xl">&#128142;</span>
                                        <span className="font-semibold">Джекпот</span>
                                    </span>
                                    <span className="font-bold text-yellow-600">
                                        {Number(info?.jackpot_amount || 0).toFixed(2)} USDT
                                    </span>
                                </div>

                                {prizeGroups.slice(0, 1).map((g) => (
                                    <div
                                        key={g.key}
                                        className="flex justify-between items-center p-3 bg-gray-50 rounded-lg"
                                    >
                                        <span className="flex items-center gap-2">
                                            <span className="text-xl">{g.icon}</span>
                                            <span>
                                                {g.from.toFixed(2)} - {g.to.toFixed(2)} USDT
                                            </span>
                                        </span>
                                        <span className={`font-bold ${g.color}`}>
                                            {g.chance >= 10 ? Math.round(g.chance) : g.chance.toFixed(1)}%
                                        </span>
                                    </div>
                                ))}
                            </div>

                            <div className="space-y-2">
                                {prizeGroups.slice(1).map((g) => (
                                    <div
                                        key={g.key}
                                        className="flex justify-between items-center p-3 bg-gray-50 rounded-lg"
                                    >
                                        <span className="flex items-center gap-2">
                                            <span className="text-xl">{g.icon}</span>
                                            <span>
                                                {g.from.toFixed(2)} - {g.to.toFixed(2)} USDT
                                            </span>
                                        </span>
                                        <span className={`font-bold ${g.color}`}>
                                            {g.chance >= 10 ? Math.round(g.chance) : g.chance.toFixed(1)}%
                                        </span>
                                    </div>
                                ))}

                                <div className={`flex justify-between items-center p-3 rounded-lg border-l-4 ${
                                    tier === 4 ? 'bg-rose-50 border-rose-500' : 'bg-violet-50 border-violet-500'
                                }`}>
                                    <span className="flex items-center gap-2">
                                        <span className="text-xl">&#128101;</span>
                                        <span className="font-semibold">Наставникам с выигрыша</span>
                                    </span>
                                    <span className={`font-bold ${tier === 4 ? 'text-rose-600' : 'text-violet-600'}`}>
                                        {refPercent}%
                                    </span>
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {/* ---------- Последние выигрыши ---------- */}
                {recentWinners.length > 0 && (
                    <div className="bg-white rounded-2xl shadow-2xl p-6 mt-6">
                        <div className="flex items-center gap-2 mb-4">
                            <Award className={`w-6 h-6 ${theme.accent}`} />
                            <h3 className={`text-xl font-bold ${theme.accent}`}>Последние выигрыши</h3>
                            {stats?.total_tickets_sold > 0 && (
                                <span className="text-xs text-gray-400 ml-auto">
                                    всего билетов: {stats.total_tickets_sold}
                                </span>
                            )}
                        </div>
                        <div className="space-y-3 max-h-96 overflow-y-auto">
                            {recentWinners.slice(0, 10).map((winner, index) => (
                                <div
                                    key={index}
                                    className={`flex items-center justify-between p-4 rounded-lg ${
                                        winner.prize_type === 'jackpot'
                                            ? 'bg-gradient-to-r from-yellow-50 to-orange-50 border-l-4 border-yellow-500'
                                            : 'bg-gray-50'
                                    }`}
                                >
                                    <div className="flex items-center gap-3 min-w-0">
                                        <div className="text-3xl flex-shrink-0">
                                            {winner.prize_type === 'jackpot' ? '💎' : '💰'}
                                        </div>
                                        <div className="min-w-0">
                                            <p className="font-semibold text-gray-800 truncate">
                                                <Nt>{winner.user_login}</Nt>
                                            </p>
                                            {winner.won_at && (
                                                <p className="text-xs text-gray-500 flex items-center gap-1 mt-1">
                                                    <Clock className="w-3 h-3" />
                                                    {formatTimeAgo(winner.won_at)}
                                                </p>
                                            )}
                                        </div>
                                    </div>
                                    <div className={`text-right font-bold flex-shrink-0 ml-2 ${
                                        winner.prize_type === 'jackpot'
                                            ? 'text-yellow-600 text-xl'
                                            : 'text-green-600'
                                    }`}>
                                        {Number(winner.net_amount ?? winner.prize_amount ?? 0).toFixed(2)} USDT
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                {/* ---------- Модалка истории ---------- */}
                {showHistory && (
                    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
                        <div className="bg-white rounded-2xl max-w-4xl w-full max-h-[85vh] overflow-hidden flex flex-col">
                            <div className="flex justify-between items-center p-6 border-b flex-shrink-0">
                                <h3 className={`text-2xl font-bold ${theme.accent}`}>
                                    {theme.name}
                                </h3>
                                <button
                                    onClick={() => setShowHistory(false)}
                                    aria-label="Закрыть"
                                    className="text-gray-500 hover:text-gray-700 transition-colors"
                                >
                                    <X className="w-6 h-6" />
                                </button>
                            </div>

                            <div className="flex border-b flex-shrink-0">
                                <button
                                    onClick={() => openHistory('tickets')}
                                    className={`flex-1 py-3 text-sm font-semibold transition-colors ${
                                        historyTab === 'tickets'
                                            ? `${theme.accent} border-b-2 border-current`
                                            : 'text-gray-500 hover:text-gray-700'
                                    }`}
                                >
                                    Мои билеты
                                </button>
                                <button
                                    onClick={() => openHistory('referrals')}
                                    className={`flex-1 py-3 text-sm font-semibold transition-colors ${
                                        historyTab === 'referrals'
                                            ? `${theme.accent} border-b-2 border-current`
                                            : 'text-gray-500 hover:text-gray-700'
                                    }`}
                                >
                                    Реферальные начисления
                                </button>
                            </div>

                            <div className="p-6 overflow-y-auto flex-1">
                                {historyTab === 'tickets' && (
                                    ticketHistory.length > 0 ? (
                                        <div className="space-y-4">
                                            {ticketHistory.map((ticket) => (
                                                <div key={ticket.id} className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                                                    <div className="flex justify-between items-start mb-2 gap-2">
                                                        <div className="min-w-0">
                                                            <p className="text-sm text-gray-600">
                                                                {new Date(ticket.created_at).toLocaleString('ru-RU')}
                                                            </p>
                                                            <p className="text-xs text-gray-400 mt-1 truncate">
                                                                {ticket.ticket_uuid}
                                                            </p>
                                                        </div>
                                                        <span className={`px-3 py-1 rounded-full text-xs font-semibold flex-shrink-0 ${
                                                            ticket.status === 'completed'
                                                                ? 'bg-green-100 text-green-700'
                                                                : 'bg-yellow-100 text-yellow-700'
                                                        }`}>
                                                            {ticket.status === 'completed' ? 'Завершён' : 'Активен'}
                                                        </span>
                                                    </div>

                                                    {ticket.net_prize_amount > 0 && (
                                                        <div className="bg-white rounded p-3 mt-2">
                                                            <p className="text-sm text-gray-600">Зачислено</p>
                                                            <p className="text-xl font-bold text-green-600">
                                                                {Number(ticket.net_prize_amount).toFixed(2)} USDT
                                                            </p>
                                                            {ticket.referral_total > 0 && (
                                                                <p className="text-xs text-gray-500 mt-1">
                                                                    номинал {Number(ticket.prize_amount).toFixed(2)} ·
                                                                    наставникам {Number(ticket.referral_total).toFixed(2)}
                                                                </p>
                                                            )}
                                                        </div>
                                                    )}

                                                    {ticket.status !== 'completed' && (
                                                        <button
                                                            onClick={() => restoreTicket(ticket)}
                                                            disabled={gameState !== 'idle' || isProcessing}
                                                            className={`w-full mt-3 py-2 px-4 rounded-lg font-semibold transition-all ${
                                                                gameState !== 'idle'
                                                                    ? 'bg-gray-200 text-gray-500 cursor-not-allowed'
                                                                    : `bg-gradient-to-r ${theme.buttonFrom} ${theme.buttonTo} text-white hover:shadow-lg`
                                                            }`}
                                                        >
                                                            {gameState !== 'idle'
                                                                ? 'Сначала завершите текущий билет'
                                                                : 'Вернуться к билету'}
                                                        </button>
                                                    )}
                                                </div>
                                            ))}
                                        </div>
                                    ) : (
                                        <div className="text-center py-12">
                                            <AlertCircle className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                                            <p className="text-gray-500">Здесь появятся ваши билеты</p>
                                        </div>
                                    )
                                )}

                                {historyTab === 'referrals' && (
                                    referralHistory.length > 0 ? (
                                        <div className="space-y-3">
                                            {referralHistory.map((payout) => (
                                                <div
                                                    key={payout.id}
                                                    className="flex items-center justify-between p-4 bg-gray-50 rounded-lg gap-2"
                                                >
                                                    <div className="min-w-0">
                                                        <p className="font-semibold text-gray-800 truncate">
                                                            <Nt>{payout.from_user?.login || 'Партнёр'}</Nt>
                                                        </p>
                                                        <p className="text-xs text-gray-500 mt-0.5">
                                                            {payout.level}-й уровень · с выигрыша{' '}
                                                            {Number(payout.prize_amount).toFixed(2)} USDT
                                                        </p>
                                                        <p className="text-xs text-gray-400 flex items-center gap-1 mt-1">
                                                            <Clock className="w-3 h-3" />
                                                            {formatTimeAgo(payout.created_at)}
                                                        </p>
                                                    </div>
                                                    <div className="text-right font-bold text-green-600 flex-shrink-0">
                                                        +{Number(payout.amount).toFixed(2)}
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    ) : (
                                        <div className="text-center py-12">
                                            <Users className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                                            <p className="text-gray-500 mb-1">Начислений пока нет</p>
                                            <p className="text-sm text-gray-400">
                                                Вы получите {levelPercent}% с каждого выигрыша партнёров
                                                на трёх уровнях вашей структуры
                                            </p>
                                        </div>
                                    )
                                )}
                            </div>
                        </div>
                    </div>
                )}

            </div>
        </div>
    );
};

export default LightLotoProCluster;
